import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { FileUp, Lightbulb, Search } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { productsQuery } from '@/api/queries'
import { rpc } from '@/api/rpc'
import type { ImportDiff } from '@/api/types'
import { can } from '@/auth/me'
import { YesNo } from '@/components/form/choice'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardTitle } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/field'
import { searchProducts } from '@/features/products/search'
import { canSeePrices } from '@/features/prices/estimates-panel'
import { parseCsv } from '@/lib/csv'
import { formatZar } from '@/lib/format'

export const Route = createFileRoute('/_app/products')({ component: Products })

/** Product catalogue. Prices only for Mo and the super admin, when pricing is on. Works offline. */
function Products() {
  const { me } = Route.useRouteContext()
  const manage = can(me, 'products.manage') && !me.impersonating
  const { data } = useQuery(productsQuery())
  const showPrices = canSeePrices(me)
  const prices = useQuery({
    queryKey: ['prices'],
    queryFn: () => rpc<Record<string, number>>('list_prices'),
    enabled: showPrices,
  })
  const [q, setQ] = useState('')
  const list = useMemo(
    () => (q ? searchProducts(data ?? [], q, 100) : (data ?? []).slice(0, 100)),
    [data, q],
  )

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <h1 className="text-3xl uppercase">Products</h1>
      {manage && <ImportProducts />}
      {manage && showPrices && <ImportPrices />}
      <div className="relative">
        <Search
          className="pointer-events-none absolute top-1/2 left-4 size-5 -translate-y-1/2 text-fg-muted"
          aria-hidden
        />
        <Input
          aria-label="Search products"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={`Search ${data?.length ?? ''} products by name, SKU, category or watts`}
          className="pl-12"
        />
      </div>
      {list.length === 0 && <EmptyState icon={Lightbulb} title="No products" />}
      <ul className="grid gap-3 sm:grid-cols-2">
        {list.map((p) => (
          <li key={p.id}>
            <Card className="flex h-full gap-3">
              <span className="flex size-20 shrink-0 items-center justify-center rounded-control border border-border bg-white p-1.5">
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    width={80}
                    height={80}
                    className="size-full object-contain"
                  />
                ) : (
                  <Lightbulb className="size-6 text-[#9ba096]" aria-hidden />
                )}
              </span>
              <div className="min-w-0">
                <p className="font-semibold">{p.name}</p>
                <p className="text-sm text-fg-muted">{[p.sku, p.category].join(' - ')}</p>
                <div className="mt-1 flex flex-wrap gap-1">
                  {showPrices && prices.data?.[p.id] != null && (
                    <Badge tone="brand">BD {formatZar(Number(prices.data[p.id]))}</Badge>
                  )}
                  {p.watts && <Badge tone="outline">{p.watts}W</Badge>}
                  {p.lumens && <Badge tone="outline">{p.lumens} lm</Badge>}
                  {p.cct && <Badge tone="outline">{p.cct}</Badge>}
                  {p.ip && <Badge tone="outline">{p.ip}</Badge>}
                  {p.warranty_years && <Badge tone="outline">{p.warranty_years} yr</Badge>}
                </div>
              </div>
            </Card>
          </li>
        ))}
      </ul>
    </div>
  )
}

function ImportProducts() {
  const queryClient = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [rows, setRows] = useState<Record<string, string>[] | null>(null)
  const [archive, setArchive] = useState(false)
  const preview = useMutation({
    mutationFn: (r: Record<string, string>[]) =>
      rpc<ImportDiff>('import_products', {
        product_rows: r,
        apply: false,
        archive_missing: archive,
      }),
  })
  const apply = useMutation({
    mutationFn: () =>
      rpc<ImportDiff>('import_products', {
        product_rows: rows,
        apply: true,
        archive_missing: archive,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['products'] }),
  })

  const load = async (f: File | undefined) => {
    if (!f) return
    const parsed = parseCsv(await f.text())
    setRows(parsed)
    apply.reset()
    preview.mutate(parsed)
  }
  const d = preview.data

  return (
    <Card className="flex flex-col gap-4">
      <CardTitle>Import products</CardTitle>
      <p className="text-fg-muted">
        Upload a CSV (for example seed/products.csv from the price list). You will see what changes
        before anything is saved. Prices are never imported here.
      </p>
      <Button variant="outline" className="self-start" onClick={() => input.current?.click()}>
        <FileUp aria-hidden /> Choose CSV
      </Button>
      <input
        ref={input}
        type="file"
        accept=".csv,text/csv"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => void load(e.target.files?.[0])}
      />
      {rows && (
        <YesNo
          label="Archive products missing from this file"
          value={archive}
          onChange={(v) => {
            setArchive(v)
            preview.mutate(rows)
          }}
        />
      )}
      {preview.error && (
        <p role="alert" className="font-semibold text-danger">
          {preview.error.message}
        </p>
      )}
      {d && !apply.isSuccess && (
        <div className="flex flex-col gap-3">
          <p className="font-semibold">
            {d.added.length} new, {d.changed.length} changed, {d.unchanged} unchanged
            {archive && `, ${d.archived.length} to archive`}
            {d.errors.length > 0 && `, ${d.errors.length} rows with problems`}
          </p>
          {d.changed.slice(0, 20).map((c) => (
            <p key={c.sku} className="text-sm">
              <strong>{c.sku}</strong>:{' '}
              {Object.entries(c.fields)
                .map(([k, [a, b]]) => `${k} ${a ?? '-'} to ${b ?? '-'}`)
                .join('; ')}
            </p>
          ))}
          {d.errors.slice(0, 10).map((e) => (
            <p key={e.row} className="text-sm text-danger">
              Row {e.row}: {e.error}
            </p>
          ))}
          <Button className="self-start" onClick={() => apply.mutate()} disabled={apply.isPending}>
            Apply import
          </Button>
        </div>
      )}
      {apply.isSuccess && (
        <p role="status" className="font-semibold text-success-fg">
          Import applied.
        </p>
      )}
      {apply.error && (
        <p role="alert" className="font-semibold text-danger">
          {apply.error.message}
        </p>
      )}
    </Card>
  )
}

/** BD (or other tier) price import: CSV with sku and amount_zar. Preview first, then apply. */
function ImportPrices() {
  const queryClient = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [rows, setRows] = useState<Record<string, string>[] | null>(null)
  const [effective, setEffective] = useState(new Date().toISOString().slice(0, 10))
  type Res = {
    added: number
    changed: number
    unchanged: number
    unknown: string[]
    applied: boolean
  }
  const run = useMutation({
    mutationFn: ({ apply, list }: { apply: boolean; list: Record<string, string>[] }) =>
      rpc<Res>('import_prices', { price_rows: list, ptype: 'BD', effective, apply }),
    onSuccess: (r) => r.applied && void queryClient.invalidateQueries({ queryKey: ['prices'] }),
  })
  const r = run.data
  return (
    <Card className="flex flex-col gap-4">
      <CardTitle>Import BD prices</CardTitle>
      <p className="text-fg-muted">
        CSV with sku and amount_zar. Old prices are kept, so earlier estimates stay the same.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-sm font-semibold">
          Effective from
          <Input
            type="date"
            value={effective}
            onChange={(e) => setEffective(e.target.value)}
            className="w-44"
          />
        </label>
        <Button variant="outline" onClick={() => input.current?.click()}>
          <FileUp aria-hidden /> Choose CSV
        </Button>
      </div>
      <input
        ref={input}
        type="file"
        accept=".csv,text/csv"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={async (e) => {
          const f = e.target.files?.[0]
          if (!f) return
          const parsed = parseCsv(await f.text())
          setRows(parsed)
          run.reset()
          run.mutate({ apply: false, list: parsed })
        }}
      />
      {r && (
        <p className="font-semibold">
          {r.added} new, {r.changed} changed, {r.unchanged} unchanged
          {r.unknown.length > 0 && `, ${r.unknown.length} unknown SKUs`}
          {r.applied && ' - applied'}
        </p>
      )}
      {r && !r.applied && (
        <Button
          className="self-start"
          onClick={() => rows && run.mutate({ apply: true, list: rows })}
          disabled={run.isPending}
        >
          Apply prices
        </Button>
      )}
      {run.error && (
        <p role="alert" className="font-semibold text-danger">
          {run.error.message}
        </p>
      )}
    </Card>
  )
}
